"use client"
import { useBlockExplorer } from "@opensea/app-kit"
import { graphql, useQuery } from "@opensea/graphql"
import { ModalFooterButton } from "@opensea/ui-kit"
import { AttachMoney } from "@opensea/ui-kit/icons"
import { useState } from "react"
import { useTranslations } from "use-intl"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants"
import { useAddress } from "../../../polychain/hooks"
import type {
  SchedulableResponse,
  SchedulableTransactionResponse,
} from "../../../polychain/hooks/useScheduler"
import { useScheduler } from "../../../polychain/hooks/useScheduler"
import { useScheduler_actionFragment } from "../../../polychain/hooks/useScheduler.fragment"
import type { TransactionIdentifier } from "../../../stores/currentTransactionStore"
import { FlowActions } from "../../../stores/flowStore"
import type { SetRoyaltyInfoFlowState } from "../../flow-state"
import {
  FeesContentLayout,
  FeesContentLayoutError,
  FeesContentLayoutSkeleton,
  WalletStatusIndicator,
} from "../common"

type SetRoyaltyInfoFlowProps = SetRoyaltyInfoFlowState

const SetRoyaltyInfoQuery = graphql(
  `
    query SetRoyaltyInfoQuery(
      $address: Address!
      $collectionSlug: String!
      $feeInput: CollectionFeeInput!
    ) {
      setRoyaltyInfoActions(
        address: $address
        collectionSlug: $collectionSlug
        feeInput: $feeInput
      ) {
        actions {
          ...useScheduler_action
        }
      }
    }
  `,
  [useScheduler_actionFragment],
)

export const SetRoyaltyInfoFlow = ({
  collectionSlug,
  fee,
}: SetRoyaltyInfoFlowProps) => {
  const t = useTranslations("wallet.SetRoyaltyInfoFlow")
  const address = useAddress()
  const [transactionIdentifier, setTransactionIdentifier] =
    useState<TransactionIdentifier>()
  const { getTransactionUrl, getName } = useBlockExplorer(
    transactionIdentifier?.chain,
  )

  const [{ data, fetching, error }] = useQuery({
    query: SetRoyaltyInfoQuery,
    variables: {
      address: address ?? "",
      collectionSlug,
      feeInput: {
        address: fee.recipientAddress,
        basisPoints: fee.basisPoints,
      },
    },
    pause: !address,
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })

  const actions = data?.setRoyaltyInfoActions.actions || []

  const {
    start,
    isPending,
    isLoading,
    isDone,
    error: schedulerError,
  } = useScheduler({
    actions,
    onAllActionsCompleted: async (lastResponse: SchedulableResponse) => {
      const tx = lastResponse as SchedulableTransactionResponse
      setTransactionIdentifier(tx.transactionIdentifier)
      FlowActions.onSuccess()
    },
    autoStartEmbeddedWallet: true,
  })

  // Early returns for loading and error states
  if (fetching) {
    return <FeesContentLayoutSkeleton />
  }

  if (error || !data?.setRoyaltyInfoActions.actions.length) {
    return (
      <FeesContentLayoutError
        footer={
          <ModalFooterButton onClick={FlowActions.endFlow} variant="secondary">
            {t("close")}
          </ModalFooterButton>
        }
        icon={AttachMoney}
        title={t("title")}
      />
    )
  }

  const state = isDone
    ? "success"
    : isPending || isLoading
      ? "pending"
      : "error"

  if (state === "success") {
    const explorerUrl = transactionIdentifier
      ? getTransactionUrl(transactionIdentifier.hash)
      : ""

    return (
      <FeesContentLayout
        body={t("successDescription", { collectionName: collectionSlug })}
        footer={
          <>
            {explorerUrl && (
              <ModalFooterButton
                href={explorerUrl}
                target="_blank"
                variant="secondary"
              >
                {t("viewOnEtherscan", { explorerName: getName() })}
              </ModalFooterButton>
            )}
            <ModalFooterButton onClick={FlowActions.endFlow}>
              {t("close")}
            </ModalFooterButton>
          </>
        }
        icon={AttachMoney}
        title={t("successTitle")}
        walletStatus={
          <WalletStatusIndicator state="complete">
            {t("updateComplete")}
          </WalletStatusIndicator>
        }
      />
    )
  }

  return (
    <FeesContentLayout
      body={t("description")}
      footer={
        <ModalFooterButton onClick={FlowActions.endFlow} variant="secondary">
          {t("close")}
        </ModalFooterButton>
      }
      icon={AttachMoney}
      title={t("title")}
      walletStatus={
        <WalletStatusIndicator onClick={start} state={state}>
          {isLoading && t("accepting")}
          {isPending && t("acceptInWallet")}
          {schedulerError && String(schedulerError)}
        </WalletStatusIndicator>
      }
    />
  )
}
